/**
 * InstantSearch Component
 */

import './styles.scss';

import { isEqual } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import HotKeys from 'react-hot-keys';
import { VelocityComponent } from 'velocity-react';

import { IQueryStringContext, QueryStringContext } from '../../../../contexts/queryString';
import { getSearchFilters, getTaxonomyQuery } from '../../../../contexts/queryString/selectors';
import Button from '../../../atoms/buttons/Button';
import { IconSearch, IconX } from '../../../atoms/images/Icon';
import { ResultList } from './ResultList';

import tr from '../../../../locales';

interface IOwnProps extends Pick<IQueryStringContext, 'setSearchQuery'> {
  filters: SearchFilter[];
  taxonomyQuery?: GQL.ITaxonomyQuery;
  initialInput: string;
  onSearchInput?: React.ChangeEventHandler<HTMLInputElement>;
  onClose(): void;
}

type IInstantSearchProps = IOwnProps;
export type IInstantSearchExposedProps = Omit<IOwnProps, 'filters' | 'setSearchQuery'>;

interface IInstantSearchState {
  closing: boolean;
  searchInput: string;
}

const classes = new BEM('instant-search-v2');

class InternalInstantSearch extends React.Component<IInstantSearchProps, IInstantSearchState> {

  closeTimeout: number;

  constructor(props) {
    super(props);

    this.state = {
      closing: false,
      searchInput: this.props.initialInput,
    };
  }

  componentDidUpdate(prevProps: IInstantSearchProps) {
    if (this.props.filters.length > prevProps.filters.length ||
      !isEqual(this.props.taxonomyQuery, prevProps.taxonomyQuery)
      ) {
      this.onClickClose();
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.closeTimeout);
  }

  onSearchInput: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    e.persist();
    this.setState({ searchInput: e.target.value }, () => {
      if (this.props.onSearchInput && this.state.searchInput !== '') {
        this.props.onSearchInput(e);
      }
    });
  }

  onClickShowAll = () => {
    this.onClickClose();
    this.props.setSearchQuery(this.state.searchInput);
  }

  onClickClose = () => {
    this.setState({ closing: true }, () => {
      this.closeTimeout = window.setTimeout(() => {
        this.props.onClose();
      }, 500);
    });
  }

  render() {
    return (
      <HotKeys keyName="esc" onKeyDown={this.onClickClose}>
        <VelocityComponent
          animation={this.state.closing ? 'transition.fadeOut' : 'transition.fadeIn'}
          display="flex"
          duration={150}
          runOnMount
        >
          <div {...classes() }>
            <VelocityComponent
              animation="transition.slideDownBigIn"
              display="flex"
              duration={250}
              runOnMount
            >
              <div {...classes('input')}>
                <HotKeys keyName="enter" onKeyDown={this.onClickShowAll}>
                  <input
                    type="text"
                    placeholder={tr.search.placeholder}
                    value={this.state.searchInput}
                    onChange={this.onSearchInput}
                    autoFocus
                    {...classes('input__textfield') }
                  />
                </HotKeys>
                <Button
                  label="Show All Results"
                  size="xsmall"
                  variant="pill"
                  colorScheme="light"
                  icon={<IconSearch />}
                  iconPosition="right"
                  onClick={this.onClickShowAll}
                  {...classes('input__button', 'show-all')}
                />
                <Button
                  variant="icon"
                  icon={<IconX />}
                  size="large"
                  colorScheme="transparent"
                  textColor="bw5"
                  onClick={this.onClickClose}
                  {...classes('input__button', 'close')}
                />
              </div>
            </VelocityComponent>
            <ResultList query={this.state.searchInput} {...classes('results')} />
          </div>
        </VelocityComponent>
      </HotKeys>
    );
  }
}

export const InstantSearch: React.SFC<IInstantSearchExposedProps> = (props) => (
  <QueryStringContext.Consumer>
    {(ctx) => (
      <InternalInstantSearch
        filters={getSearchFilters(ctx)}
        taxonomyQuery={getTaxonomyQuery(ctx)}
        setSearchQuery={ctx.setSearchQuery}
        {...props}
      />
    )}
  </QueryStringContext.Consumer>
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/global/InstantSearch/index.tsx